import { ApplicationStatusEnumLabels } from "@boundless/bl-api-client/lender/models/ApplicationStatusEnumLabels"
import {
  getLenderApplicationsRetrieveQueryKey,
  useLenderApplicationsPartialUpdate,
} from "@boundless/bl-api-client/lender/services"
import { LenderApplicationsPartialUpdateBody } from "@boundless/bl-api-client/lender/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { RefreshCwIcon } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"

import { FormInput } from "@/components/form-elements/FormInput"
import { FormTextArea } from "@/components/form-elements/FormTextArea"
import { Button, LoadingButton } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"

import type {
  Application,
  ApplicationStatusEnum,
} from "@boundless/bl-api-client/lender/models"
import type { z } from "zod"

const formSchemaObject = LenderApplicationsPartialUpdateBody.pick({
  boundlessCommission: true,
  fee: true,
  maxAmountOffer: true,
  nextStep: true,
  term: true,
})
type FormSchema = z.infer<typeof formSchemaObject>

export function ApplicationStatusCard({
  application,
}: {
  application: Application
}) {
  const queryClient = useQueryClient()

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchemaObject),
    defaultValues: formSchemaObject.parse(application),
  })

  const {
    mutate: updateApplication,
    isPending,
    variables,
  } = useLenderApplicationsPartialUpdate({
    mutation: {
      onSuccess: (newValues) => {
        const queryKey = getLenderApplicationsRetrieveQueryKey(newValues.uuid)
        queryClient.setQueryData(queryKey, newValues)
        toast.success("Application updated!")
        form.reset(formSchemaObject.parse(newValues))
      },
      onError: (error: ErrorType<ErrorResponseType>) => {
        applyErrorsToForm(form.setError, error)
      },
    },
  })

  const handleSubmit = (data: FormSchema) => {
    updateApplication({ uuid: application.uuid, data })
  }

  const handleStatusChange = (status: ApplicationStatusEnum) => {
    updateApplication({ uuid: application.uuid, data: { status } })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Application Status</CardTitle>
      </CardHeader>
      <Form {...form}>
        <form id="status-form" onSubmit={form.handleSubmit(handleSubmit)}>
          <CardContent className="flex flex-col gap-6">
            <ApplicationStatusButtons
              status={application.status || undefined}
              onChange={handleStatusChange}
              optimisticStatus={variables?.data?.status || undefined}
              isPending={isPending}
            />
            <div className="flex flex-row gap-6">
              <div className="flex basis-1/3 flex-col gap-6">
                <FormInput
                  name="maxAmountOffer"
                  placeholder="Max Offer Amount (Interval Value)"
                  type="number"
                  setValueAsString
                />

                {/* TODO: Include Radio Buttons for Fee Type */}
                <FormInput
                  name="fee"
                  placeholder="Fee"
                  type="number"
                  setValueAsString
                />

                <FormInput name="term" placeholder="Term" type="number" />

                <FormInput
                  name="boundlessCommission"
                  placeholder="Boundless Commission"
                  type="number"
                  setValueAsString
                />
              </div>
              <div className="basis-2/3">
                <FormTextArea name="nextStep" placeholder="Next Step" />
              </div>
            </div>
          </CardContent>
        </form>
      </Form>
      <CardFooter>
        {isPending ? (
          <LoadingButton>Update</LoadingButton>
        ) : (
          <Button
            type="submit"
            form="status-form"
            disabled={!form.formState.isDirty}
          >
            <RefreshCwIcon />
            Update
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}

interface StatusButtonsProps {
  isPending: boolean
  onChange: (newStatus: ApplicationStatusEnum) => void
  optimisticStatus: ApplicationStatusEnum | undefined
  status: ApplicationStatusEnum | undefined
}

function ApplicationStatusButtons({
  status,
  onChange,
  optimisticStatus,
  isPending,
}: StatusButtonsProps) {
  return (
    <div className="flex w-full justify-between gap-2">
      {Object.entries(ApplicationStatusEnumLabels).map(([key, label]) => {
        const isActive = key === status
        const isOptimistic =
          key === optimisticStatus && optimisticStatus !== status && isPending
        const ButtonComponent = isOptimistic ? LoadingButton : Button

        return (
          <ButtonComponent
            className="min-w-[120px] flex-1"
            disabled={isPending}
            key={key}
            onClick={() => onChange(key as ApplicationStatusEnum)}
            type="button"
            variant={isActive ? "default" : "outline"}
          >
            {label}
          </ButtonComponent>
        )
      })}
    </div>
  )
}
